import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { LangProvider } from "@/i18n";
import { ThemeProvider } from "@/lib/theme";
import { Nav } from "@/components/Nav";
import { Footer } from "@/components/Footer";
import Home from "@/pages/Home";
import Streamers from "@/pages/Streamers";
import Contattaci from "@/pages/Contattaci";
import { DonateFab } from "@/components/DonateFab";
import YouTubePage from "@/pages/YouTubePage";
import EventiPage from "@/pages/Eventi";
import EventRoom from "@/pages/EventRoom";
import NewsPage from "@/pages/News";
import NewsArticle from "@/pages/NewsArticle";
import Classifica from "@/pages/Classifica";
import Giveaway from "@/pages/Giveaway";
import Admin from "@/pages/Admin";
import Donazione from "@/pages/Donazione";
import MediaKit from "@/pages/MediaKit";
import Pubblicita from "@/pages/Pubblicita";
import Lagofast from "@/pages/Lagofast";
import DiscordPage from "@/pages/DiscordPage";
import Profilo from "@/pages/Profilo";
import AuthCallback from "@/pages/AuthCallback";
import Operatore from "@/pages/Operatore";
import CountdownOverlay from "@/pages/CountdownOverlay";
import PromoOverlay from "@/pages/PromoOverlay";
import GiveawayObs from "@/pages/GiveawayObs";
import { Termini, Privacy, EliminaDati, Copyright } from "@/pages/Legal";

function GlitchIntro() {
  const [gone, setGone] = useState(false);
  useEffect(() => {
    const id = setTimeout(() => setGone(true), 2100);
    return () => clearTimeout(id);
  }, []);
  if (gone) return null;
  return (
    <div className="glitch-intro" data-testid="site-intro">
      <div className="text-center px-5">
        <p className="glitch-text font-display text-6xl md:text-8xl uppercase" data-text="WARDOGS.HQ" data-testid="site-intro-text">WARDOGS.HQ</p>
        <p className="glitch-sub font-mono text-[10px] md:text-xs uppercase tracking-[0.5em] text-wardogs mt-4">// system boot — italian headquarters</p>
      </div>
    </div>
  );
}

function SiteShell() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  if (location.pathname === "/countdown") {
    return <CountdownOverlay />;
  }
  if (location.pathname === "/promo") {
    return <PromoOverlay />;
  }
  if (location.pathname === "/giveaway/obs") {
    return <GiveawayObs />;
  }
  return (
    <>
      <CopyShield />
      <WheelRails />
      <Nav />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/streamer" element={<Streamers />} />
        <Route path="/contattaci" element={<Contattaci />} />
        <Route path="/youtube" element={<YouTubePage />} />
        <Route path="/community" element={<Navigate to="/" replace />} />
        <Route path="/giveaway" element={<Giveaway />} />
        <Route path="/news" element={<NewsPage />} />
        <Route path="/news/:id" element={<NewsArticle />} />
        <Route path="/eventi" element={<EventiPage />} />
        <Route path="/eventi/:slug" element={<EventRoom />} />
        <Route path="/classifica" element={<Classifica />} />
        <Route path="/discord" element={<DiscordPage />} />
        <Route path="/donazione" element={<Donazione />} />
        <Route path="/media" element={<MediaKit />} />
        <Route path="/pubblicita" element={<Pubblicita />} />
        <Route path="/lagofast" element={<Lagofast />} />
        <Route path="/profilo" element={<Profilo />} />
        <Route path="/operatore/:login" element={<Operatore />} />
        <Route path="/termini" element={<Termini />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/elimina-dati" element={<EliminaDati />} />
        <Route path="/copyright" element={<Copyright />} />
        <Route path="/admin" element={<Admin />} />
      </Routes>
      <Footer />
      <DonateFab />
    </>
  );
}

// Scudo anticopia: i contenuti del sito sono di proprieta' esclusiva.
// Blocca tasto destro, selezione, copia/taglia, trascinamento immagini e
// Ctrl+S/U (salva/sorgente). ECCEZIONI: campi di testo (Staff/editing), le
// aree .allow-copy (pagina /pubblicita) e lo STAFF LOGGATO (is_staff):
// chi modera deve poter copiare testi, link e immagini liberamente.
function CopyShield() {
  const { user } = useAuth();
  const exempt = !!user?.is_staff;
  useEffect(() => {
    if (exempt) return; // staff: scudo completamente disattivato
    const isForm = (t) => t?.closest?.("input, textarea, [contenteditable]");
    const isAllowed = (t) => isForm(t) || t?.closest?.(".allow-copy");
    const blockUnlessAllowed = (e) => { if (!isAllowed(e.target)) e.preventDefault(); };
    const onCopy = (e) => {
      const node = document.getSelection()?.anchorNode;
      const el = node?.nodeType === 1 ? node : node?.parentElement;
      if (!(el?.closest?.(".allow-copy") || isForm(el))) e.preventDefault();
    };
    const onKey = (e) => {
      const k = (e.key || "").toLowerCase();
      if ((e.ctrlKey || e.metaKey) && ["s", "u"].includes(k)) e.preventDefault();
    };
    document.addEventListener("contextmenu", blockUnlessAllowed);
    document.addEventListener("selectstart", blockUnlessAllowed);
    document.addEventListener("copy", onCopy);
    document.addEventListener("cut", blockUnlessAllowed);
    document.addEventListener("dragstart", blockUnlessAllowed);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("contextmenu", blockUnlessAllowed);
      document.removeEventListener("selectstart", blockUnlessAllowed);
      document.removeEventListener("copy", onCopy);
      document.removeEventListener("cut", blockUnlessAllowed);
      document.removeEventListener("dragstart", blockUnlessAllowed);
      document.removeEventListener("keydown", onKey);
    };
  }, [exempt]);
  return null;
}

// Rotellina del mouse: sulle rotaie orizzontali marcate .wheel-x (tab Staff,
// rail clip, slider) converte lo scorrimento verticale in orizzontale;
// ai bordi della rotaia la rotellina torna a scorrere la pagina.
// (Le liste verticali interne usano data-lenis-prevent per lo scroll nativo.)
function WheelRails() {
  useEffect(() => {
    const onWheel = (e) => {
      const rail = e.target.closest?.(".wheel-x");
      if (!rail || rail.scrollWidth <= rail.clientWidth) return;
      const delta = Math.abs(e.deltaY) >= Math.abs(e.deltaX) ? e.deltaY : e.deltaX;
      const atStart = rail.scrollLeft <= 0 && delta < 0;
      const atEnd = rail.scrollLeft + rail.clientWidth >= rail.scrollWidth - 1 && delta > 0;
      if (atStart || atEnd) return;
      rail.scrollLeft += delta;
      e.preventDefault();
    };
    document.addEventListener("wheel", onWheel, { passive: false });
    return () => document.removeEventListener("wheel", onWheel);
  }, []);
  return null;
}

function App() {
  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09, wheelMultiplier: 1.05 });
    let frame;
    const raf = (time) => {
      lenis.raf(time);
      frame = requestAnimationFrame(raf);
    };
    frame = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(frame);
      lenis.destroy();
    };
  }, []);

  return (
    <div className="App bg-[#050505] min-h-screen text-white">
      <div className="noise-overlay" />
      <GlitchIntro />
      <ThemeProvider>
        <LangProvider>
          <AuthProvider>
            <BrowserRouter>
              <SiteShell />
            </BrowserRouter>
          </AuthProvider>
        </LangProvider>
      </ThemeProvider>
      <Toaster
        theme="dark"
        position="bottom-right"
        toastOptions={{
          style: { background: "#0C0C0C", border: "1px solid #1A1A1A", borderRadius: 0, color: "#fff" },
        }}
      />
    </div>
  );
}

export default App;
